import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import {
  Search, MapPin, BadgeCheck, X, SlidersHorizontal, Wifi, Building2,
  RotateCcw, AlertCircle, SearchX, ArrowRight,
} from "lucide-react";
import {
  PROFISSIONAIS_EXEMPLO, ETAPAS_FILTRO, CATEGORIAS_FILTRO, CIDADES_FILTRO,
  IDIOMAS_FILTRO, CATEGORIA_COR, COR_CLASSES, getInitials,
} from "@/data/profissionaisData";

const EMPTY = { nome: "", etapa: "", categoria: "", cidade: "", bairro: "", idioma: "", online: false, presencial: false, particular: false, domiciliar: false };

const selectClass =
  "w-full bg-white border border-maternnia-nevoa rounded-xl px-4 py-2.5 text-sm text-maternnia-charcoal focus:outline-none focus:ring-2 focus:ring-maternnia-pink/30 focus:border-maternnia-pink transition";

const ProfissionaisPage = () => {
  const [filters, setFilters] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error] = useState(false);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 700);
    return () => clearTimeout(t);
  }, []);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearAll = () => setFilters(EMPTY);

  const resultados = useMemo(() => {
    return PROFISSIONAIS_EXEMPLO.filter((p) => {
      if (filters.nome && !p.nome.toLowerCase().includes(filters.nome.toLowerCase())) return false;
      if (filters.etapa && !p.etapas.includes(filters.etapa)) return false;
      if (filters.categoria && p.categoria !== filters.categoria) return false;
      if (filters.cidade && p.cidade !== filters.cidade) return false;
      if (filters.bairro && !p.bairro.toLowerCase().includes(filters.bairro.toLowerCase())) return false;
      if (filters.idioma && !p.idiomas.includes(filters.idioma)) return false;
      if (filters.online && !p.online) return false;
      if (filters.presencial && !p.presencial) return false;
      if (filters.particular && !p.particular) return false;
      if (filters.domiciliar && !p.domiciliar) return false;
      return true;
    });
  }, [filters]);

  const activeChips = useMemo(() => {
    const chips = [];
    if (filters.nome) chips.push({ k: "nome", label: `"${filters.nome}"` });
    if (filters.etapa) chips.push({ k: "etapa", label: filters.etapa });
    if (filters.categoria) chips.push({ k: "categoria", label: filters.categoria });
    if (filters.cidade) chips.push({ k: "cidade", label: filters.cidade });
    if (filters.bairro) chips.push({ k: "bairro", label: filters.bairro });
    if (filters.idioma) chips.push({ k: "idioma", label: filters.idioma });
    if (filters.online) chips.push({ k: "online", label: "Online" });
    if (filters.presencial) chips.push({ k: "presencial", label: "Presencial" });
    if (filters.particular) chips.push({ k: "particular", label: "Particular" });
    if (filters.domiciliar) chips.push({ k: "domiciliar", label: "Domiciliar" });
    return chips;
  }, [filters]);

  const removeChip = (k) => set(k, typeof EMPTY[k] === "boolean" ? false : "");

  const Toggle = ({ k, label, icon: Icon }) => (
    <label data-testid={`toggle-${k}`} className="flex items-center gap-2.5 cursor-pointer py-1.5">
      <input type="checkbox" checked={filters[k]} onChange={(e) => set(k, e.target.checked)}
        className="h-4 w-4 rounded border-maternnia-nevoa accent-maternnia-pink" />
      <span className="text-sm text-maternnia-charcoal flex items-center gap-1.5">{Icon && <Icon size={14} className="text-maternnia-taupe" />}{label}</span>
    </label>
  );

  return (
    <main data-testid="profissionais-page" className="bg-white min-h-screen">
      <Seo
        title="Profissionais da maternidade"
        description="Encontre obstetras, doulas, pediatras, nutricionistas e outros profissionais selecionados para apoiar você em cada etapa da maternidade."
        noindex
      />
      {/* Header band */}
      <section className="bg-maternnia-porcelana">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <span className="text-sm font-semibold tracking-widest text-maternnia-pink uppercase">Diretório</span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-maternnia-charcoal mt-4 font-medium">
            Encontre profissionais e serviços
          </h1>
          <p className="mt-5 text-base md:text-lg text-maternnia-taupe leading-relaxed font-light max-w-2xl">
            Filtre por etapa, categoria, localização e tipo de atendimento para encontrar quem cuida de você com confiança.
          </p>
          <div data-testid="exemplo-banner" className="mt-7 inline-flex items-center gap-2 bg-maternnia-gold/15 text-maternnia-gold text-xs font-medium px-4 py-2.5 rounded-full">
            <AlertCircle size={14} /> Dados de exemplo para demonstração visual — perfis ainda não reais.
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-10">
          {/* FILTERS */}
          <aside className="lg:col-span-1">
            <button
              data-testid="toggle-filters-mobile"
              onClick={() => setShowFiltersMobile((v) => !v)}
              className="lg:hidden w-full flex items-center justify-between bg-maternnia-porcelana rounded-xl px-4 py-3 mb-4 text-sm font-medium text-maternnia-charcoal"
            >
              <span className="flex items-center gap-2"><SlidersHorizontal size={16} /> Filtros</span>
              <span>{showFiltersMobile ? "Ocultar" : "Mostrar"}</span>
            </button>

            <div className={`${showFiltersMobile ? "block" : "hidden"} lg:block lg:sticky lg:top-28 space-y-5 bg-white lg:bg-transparent`}>
              <div className="relative">
                <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-maternnia-taupe" />
                <input data-testid="filter-nome" type="text" value={filters.nome} onChange={(e) => set("nome", e.target.value)}
                  placeholder="Buscar por nome" className={`${selectClass} pl-10`} />
              </div>

              <select data-testid="filter-etapa" className={selectClass} value={filters.etapa} onChange={(e) => set("etapa", e.target.value)}>
                <option value="">Etapa (todas)</option>
                {ETAPAS_FILTRO.map((x) => <option key={x}>{x}</option>)}
              </select>
              <select data-testid="filter-categoria" className={selectClass} value={filters.categoria} onChange={(e) => set("categoria", e.target.value)}>
                <option value="">Categoria (todas)</option>
                {CATEGORIAS_FILTRO.map((x) => <option key={x}>{x}</option>)}
              </select>
              <select data-testid="filter-cidade" className={selectClass} value={filters.cidade} onChange={(e) => set("cidade", e.target.value)}>
                <option value="">Cidade (todas)</option>
                {CIDADES_FILTRO.map((x) => <option key={x}>{x}</option>)}
              </select>
              <div className="relative">
                <MapPin size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-maternnia-taupe" />
                <input data-testid="filter-bairro" type="text" value={filters.bairro} onChange={(e) => set("bairro", e.target.value)}
                  placeholder="Bairro" className={`${selectClass} pl-10`} />
              </div>
              <select data-testid="filter-idioma" className={selectClass} value={filters.idioma} onChange={(e) => set("idioma", e.target.value)}>
                <option value="">Idioma (todos)</option>
                {IDIOMAS_FILTRO.map((x) => <option key={x}>{x}</option>)}
              </select>

              <div className="border-t border-maternnia-nevoa pt-4">
                <p className="text-xs font-semibold tracking-wide text-maternnia-taupe uppercase mb-2">Atendimento</p>
                <Toggle k="online" label="Online" icon={Wifi} />
                <Toggle k="presencial" label="Presencial" icon={Building2} />
                <Toggle k="particular" label="Particular" />
                <Toggle k="domiciliar" label="Domiciliar" />
              </div>

              <button data-testid="clear-filters" onClick={clearAll}
                className="w-full inline-flex items-center justify-center gap-2 border border-maternnia-charcoal/15 text-maternnia-charcoal hover:bg-maternnia-porcelana rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300">
                <RotateCcw size={15} /> Limpar filtros
              </button>
            </div>
          </aside>

          {/* RESULTS */}
          <div className="lg:col-span-3">
            {/* active chips + count */}
            <div className="flex flex-wrap items-center gap-3 mb-7">
              <span data-testid="results-count" className="text-sm text-maternnia-taupe font-medium">
                {loading ? "Carregando..." : `${resultados.length} profissionai(s) encontrado(s)`}
              </span>
              {activeChips.map((c) => (
                <button key={c.k} data-testid={`active-filter-${c.k}`} onClick={() => removeChip(c.k)}
                  className="inline-flex items-center gap-1.5 bg-maternnia-pink/10 text-maternnia-pink text-xs font-medium rounded-full pl-3 pr-2 py-1.5 hover:bg-maternnia-pink/20 transition">
                  {c.label} <X size={13} />
                </button>
              ))}
            </div>

            {/* STATES */}
            {error ? (
              <div data-testid="prof-error" className="text-center py-24">
                <div className="h-14 w-14 mx-auto rounded-2xl bg-red-50 flex items-center justify-center mb-5">
                  <AlertCircle size={26} className="text-red-400" />
                </div>
                <h3 className="font-serif text-2xl text-maternnia-charcoal font-medium">Algo deu errado</h3>
                <p className="mt-2 text-sm text-maternnia-taupe font-light">Não foi possível carregar os profissionais. Tente novamente.</p>
              </div>
            ) : loading ? (
              <div data-testid="prof-loading" className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="rounded-3xl border border-maternnia-nevoa/60 p-7 animate-pulse">
                    <div className="flex items-center gap-4 mb-5">
                      <div className="h-14 w-14 rounded-2xl bg-maternnia-porcelana" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-2/3 bg-maternnia-porcelana rounded" />
                        <div className="h-3 w-1/3 bg-maternnia-porcelana rounded" />
                      </div>
                    </div>
                    <div className="h-3 w-full bg-maternnia-porcelana rounded mb-2" />
                    <div className="h-3 w-4/5 bg-maternnia-porcelana rounded" />
                  </div>
                ))}
              </div>
            ) : resultados.length === 0 ? (
              <div data-testid="prof-empty" className="text-center py-24">
                <div className="h-14 w-14 mx-auto rounded-2xl bg-maternnia-porcelana flex items-center justify-center mb-5">
                  <SearchX size={26} className="text-maternnia-taupe" />
                </div>
                <h3 className="font-serif text-2xl text-maternnia-charcoal font-medium">Nenhum resultado encontrado</h3>
                <p className="mt-2 text-sm text-maternnia-taupe font-light">Tente ajustar ou limpar os filtros para ver mais opções.</p>
                <button onClick={clearAll} className="mt-6 inline-flex items-center gap-2 text-maternnia-eucalipto hover:text-maternnia-floresta font-medium">
                  <RotateCcw size={15} /> Limpar filtros
                </button>
              </div>
            ) : (
              <div data-testid="prof-results" className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {resultados.map((p, i) => {
                  const cor = COR_CLASSES[CATEGORIA_COR[p.categoria]] || COR_CLASSES.pink;
                  return (
                    <motion.div
                      key={p.id}
                      data-testid={`prof-card-${p.id}`}
                      className="group bg-white rounded-3xl border border-maternnia-nevoa/60 p-7 shadow-sm hover:shadow-[0_18px_44px_rgba(43,37,38,0.10)] hover:-translate-y-1 hover:border-maternnia-pink/40 transition-all duration-300"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                    >
                      <div className="flex items-start gap-4 mb-4">
                        <div className={`h-14 w-14 rounded-2xl ${cor.bg} ${cor.text} flex items-center justify-center font-serif text-xl font-medium shrink-0 overflow-hidden transition-transform duration-300 group-hover:scale-105`}>
                          {getInitials(p.nome)}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h3 className="font-serif text-xl text-maternnia-charcoal font-medium truncate">{p.nome}</h3>
                            {p.verificado && <BadgeCheck size={17} className="text-maternnia-eucalipto shrink-0" data-testid={`verificado-${p.id}`} />}
                          </div>
                          <p className={`text-xs font-semibold uppercase tracking-wide mt-0.5 ${cor.text}`}>{p.categoria}</p>
                        </div>
                      </div>

                      <p className="text-sm text-maternnia-taupe leading-relaxed font-light mb-4">{p.descricao}</p>

                      <div className="flex flex-wrap items-center gap-2 mb-5 text-xs">
                        <span className="inline-flex items-center gap-1 text-maternnia-taupe">
                          <MapPin size={13} /> {p.bairro}, {p.cidade}
                        </span>
                        {p.online && <span className="inline-flex items-center gap-1 bg-maternnia-eucalipto/10 text-maternnia-eucalipto rounded-full px-2.5 py-1"><Wifi size={12} /> Online</span>}
                        {p.presencial && <span className="inline-flex items-center gap-1 bg-maternnia-porcelana text-maternnia-taupe rounded-full px-2.5 py-1"><Building2 size={12} /> Presencial</span>}
                      </div>

                      <Link
                        to={`/profissionais/${p.id}`}
                        data-testid={`ver-perfil-${p.id}`}
                        className="group/btn w-full inline-flex items-center justify-center gap-2 bg-maternnia-charcoal text-white hover:bg-maternnia-pink rounded-full px-6 py-3 text-sm font-medium transition-all duration-300"
                      >
                        Ver perfil
                        <ArrowRight size={16} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ProfissionaisPage;
